import { useMemo, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { motion } from 'framer-motion'
import { supabase } from '../lib/supabase'
import { useAuth } from '../hooks/useAuth'
import { useHouseholdRealtime } from '../hooks/useHouseholdRealtime'
import {
  analyzeStorePrices,
  calculateSavingsOpportunities,
  type ItemPriceComparison,
  type HouseholdSavingsOpportunity,
} from '../lib/storePriceAnalyzer'

// ─── Count-up hook ────────────────────────────────────────────────────────────

function useCountUp(target: number, duration = 900): number {
  const [value, setValue] = useState(0)
  useEffect(() => {
    if (target === 0) { setValue(0); return }
    const start = Date.now()
    let raf: number
    const frame = () => {
      const t = Math.min((Date.now() - start) / duration, 1)
      const eased = 1 - Math.pow(1 - t, 3)
      setValue(target * eased)
      if (t < 1) raf = requestAnimationFrame(frame)
    }
    raf = requestAnimationFrame(frame)
    return () => cancelAnimationFrame(raf)
  }, [target, duration])
  return value
}

// ─── OpportunityCard ──────────────────────────────────────────────────────────

function OpportunityCard({
  opp, index, salePriceMap,
}: {
  opp:          HouseholdSavingsOpportunity
  index:        number
  salePriceMap: Record<string, boolean>
}) {
  const cheapestWasSale = salePriceMap[`${opp.item.itemId}-${opp.item.cheapestStore}`] ?? false
  const maxPrice = opp.item.stores[opp.item.stores.length - 1].avgPrice

  return (
    <motion.div
      className="bg-white rounded-2xl border border-[#E8E2DC] p-4 mb-3 overflow-hidden"
      style={{ borderLeft: '3px solid #6B9E78' }}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.06, duration: 0.25 }}
    >
      {/* Header row */}
      <div className="flex items-center gap-3 mb-3">
        <span className="text-2xl">{opp.item.itemEmoji}</span>
        <div className="flex-1 min-w-0">
          <p className="font-semibold text-sm text-[#2C2A26]">{opp.item.itemName}</p>
          <p className="text-xs text-[#7A7468]">You usually buy at {opp.currentStore}</p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-sm font-bold text-[#6B9E78]">
            Save ${opp.priceDiff.toFixed(2)}
          </p>
          <p className="text-xs text-[#B0A99F]">per purchase</p>
        </div>
      </div>

      {/* Store comparison bars */}
      {opp.item.stores.map((store, i) => {
        const isChaeapest = store.storeName === opp.item.cheapestStore
        const pct         = maxPrice > 0 ? (store.avgPrice / maxPrice) * 100 : 100
        const pctAbove    = maxPrice > 0
          ? (store.avgPrice - opp.item.stores[0].avgPrice) / opp.item.stores[0].avgPrice
          : 0
        const barColor    = isChaeapest
          ? '#6B9E78'
          : pctAbove > 0.2 ? '#C25B4E' : '#D4863A'

        return (
          <div key={store.storeName} className="mb-2">
            <div className="flex justify-between items-center mb-1">
              <span className="text-xs text-[#7A7468]">
                {store.storeName}
                {isChaeapest && (
                  <span className="ml-1 font-semibold text-[#6B9E78]">cheapest</span>
                )}
              </span>
              <span className="text-xs font-semibold text-[#2C2A26]">
                ${store.avgPrice.toFixed(2)}
              </span>
            </div>
            <div className="h-1.5 bg-[#F2EDE6] rounded-full overflow-hidden">
              <motion.div
                className="h-full rounded-full"
                style={{ backgroundColor: barColor }}
                initial={{ width: 0 }}
                animate={{ width: `${pct}%` }}
                transition={{ delay: index * 0.06 + i * 0.05 + 0.2, duration: 0.5, ease: 'easeOut' }}
              />
            </div>
          </div>
        )
      })}

      {/* Save pill */}
      <div className="mt-3 flex">
        <span
          className="inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-semibold"
          style={{ background: '#EAF4ED', color: '#6B9E78' }}
        >
          Buy at {opp.item.cheapestStore} to save ${opp.priceDiff.toFixed(2)} per purchase
        </span>
      </div>

      {/* Sale price warning */}
      {cheapestWasSale && (
        <p className="text-xs text-[#D4863A] mt-2">
          ⚠️ Last recorded price was a sale — regular price may be higher
        </p>
      )}
    </motion.div>
  )
}

// ─── AllItemsSection ──────────────────────────────────────────────────────────

function AllItemCard({
  comparison,
  index,
  onTap,
  cheapestWasSale,
}: {
  comparison:      ItemPriceComparison
  index:           number
  onTap:           () => void
  cheapestWasSale: boolean
}) {
  return (
    <motion.button
      className="w-full bg-white rounded-xl border border-[#E8E2DC] p-3 flex items-center gap-3 text-left"
      initial={{ opacity: 0, x: -8 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: index * 0.04, duration: 0.2 }}
      onClick={onTap}
    >
      <span className="text-xl">{comparison.itemEmoji}</span>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-1.5">
          <p className="text-sm font-medium text-[#2C2A26] truncate">{comparison.itemName}</p>
          {cheapestWasSale && (
            <span className="text-[9px] px-1.5 py-0.5 rounded-full bg-[#FEF3E8] text-[#D4863A] font-semibold shrink-0">
              sale price
            </span>
          )}
        </div>
        <p className="text-xs text-[#7A7468]">
          {comparison.stores.length} store{comparison.stores.length !== 1 ? 's' : ''} ·
          {' '}${comparison.stores[0].avgPrice.toFixed(2)} at {comparison.stores[0].storeName}
        </p>
      </div>
      <div className="text-xs text-[#9A9491] shrink-0">→</div>
    </motion.button>
  )
}

// ─── StorePrices ──────────────────────────────────────────────────────────────

export default function StorePrices() {
  const navigate     = useNavigate()
  const queryClient  = useQueryClient()
  const { session }  = useAuth()
  const userId       = session?.user.id

  useEffect(() => { document.title = 'Store Prices — Pantry' }, [])

  // ── Household id ──────────────────────────────────────────────────────────

  const { data: userData } = useQuery({
    queryKey: ['userMeta', userId],
    enabled:  !!userId,
    queryFn:  async () => {
      const { data } = await supabase
        .from('users')
        .select('household_id')
        .eq('id', userId!)
        .single()
      return data
    },
  })

  const householdId = userData?.household_id ?? null

  useHouseholdRealtime(householdId, {
    tables: ['store_prices', 'confirmations', 'user_stores'],
    invalidateMap: {
      store_prices:  [['store-prices', householdId]],
      confirmations: [['store-confirmations', householdId]],
      user_stores:   [['user-stores', householdId]],
    },
  })

  // ── Store prices (joined with pantry_items) ───────────────────────────────

  const { data: storePricesData = [], isLoading } = useQuery({
    queryKey: ['store-prices', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      const { data } = await supabase
        .from('store_prices')
        .select('id, item_id, store_name, price, recorded_at, was_on_sale, pantry_items(name, emoji)')
        .eq('household_id', householdId!)
        .order('recorded_at', { ascending: false })
      return data ?? []
    },
  })

  // ── Recent confirmations with store name ──────────────────────────────────

  const { data: storeConfirmations = [] } = useQuery({
    queryKey: ['store-confirmations', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      const { data } = await supabase
        .from('confirmations')
        .select('item_id, store_name, confirmed_at')
        .eq('household_id', householdId!)
        .not('store_name', 'is', null)
        .order('confirmed_at', { ascending: false })
        .limit(200)
      return data ?? []
    },
  })

  // ── User stores ───────────────────────────────────────────────────────────

  const { data: userStores = [], refetch: refetchStores } = useQuery({
    queryKey: ['user-stores', householdId],
    enabled:  !!householdId,
    queryFn:  async () => {
      const { data } = await supabase
        .from('user_stores')
        .select('store_name, visit_count, last_visited, is_favorite')
        .eq('household_id', householdId!)
        .order('visit_count', { ascending: false })
      return data ?? []
    },
  })

  // ── Derived ───────────────────────────────────────────────────────────────

  // Map of `${itemId}-${storeName}` → was_on_sale for the most recent price record
  const salePriceMap = useMemo(() => {
    const seen = new Set<string>()
    const map: Record<string, boolean> = {}
    for (const row of storePricesData) {
      const key = `${row.item_id}-${row.store_name}`
      if (!seen.has(key)) {
        seen.add(key)
        map[key] = (row as any).was_on_sale ?? false
      }
    }
    return map
  }, [storePricesData])

  const comparisons   = useMemo(() => analyzeStorePrices(storePricesData), [storePricesData])
  const opportunities = useMemo(
    () => calculateSavingsOpportunities(comparisons, storeConfirmations),
    [comparisons, storeConfirmations],
  )

  const totalMonthlySaving = opportunities.reduce((s, o) => s + o.potentialMonthlySaving, 0)
  const animatedSaving     = useCountUp(totalMonthlySaving)

  // ── Toggle favorite store ─────────────────────────────────────────────────

  const toggleFavorite = useMutation({
    mutationFn: async ({ storeName, current }: { storeName: string; current: boolean }) => {
      await supabase
        .from('user_stores')
        .update({ is_favorite: !current })
        .eq('household_id', householdId!)
        .eq('store_name', storeName)
    },
    onSuccess: () => {
      refetchStores()
      queryClient.invalidateQueries({ queryKey: ['user-stores', householdId] })
    },
  })

  // ─────────────────────────────────────────────────────────────────────────

  if (isLoading) {
    return (
      <div className="theme-background min-h-screen flex items-center justify-center">
        <div className="w-10 h-10 rounded-full border-2 border-[#6B9E78] border-t-transparent animate-spin" />
      </div>
    )
  }

  return (
    <div className="theme-background min-h-screen">

      {/* Header */}
      <div className="px-4 pt-8 pb-4 flex items-center gap-3">
        <button
          onClick={() => navigate(-1)}
          className="w-8 h-8 flex items-center justify-center rounded-full
                     bg-[#F0EDE8] text-[#7A7468] hover:bg-[#E8E2DC] transition-colors text-sm"
        >
          ←
        </button>
        <div>
          <h1 className="text-xl font-semibold text-[#2C2A26]">Store Price Tracker</h1>
          <p className="text-xs text-[#9A9491]">See where to save money</p>
        </div>
      </div>

      <div className="px-4 pb-24 flex flex-col gap-5 max-w-[480px] mx-auto">

        {/* Summary card */}
        <motion.div
          className="bg-white rounded-2xl border border-[#E8E2DC] p-5"
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
        >
          {totalMonthlySaving > 0 ? (
            <>
              <p className="text-xs text-[#9A9491] uppercase tracking-wide font-semibold mb-1">
                Potential monthly saving
              </p>
              <div className="flex items-baseline gap-2">
                <span className="text-4xl font-bold" style={{ color: '#6B9E78' }}>
                  ${animatedSaving.toFixed(2)}
                </span>
                <span className="text-sm text-[#9A9491]">/mo</span>
              </div>
              <p className="text-xs text-[#7A7468] mt-1">
                by switching to the cheapest store on {opportunities.length} item{opportunities.length !== 1 ? 's' : ''}
              </p>
            </>
          ) : (
            <div className="text-center py-4">
              <p className="text-3xl mb-2">🏪</p>
              <p className="text-sm font-medium text-[#2C2A26]">
                {storePricesData.length === 0
                  ? 'No prices tracked yet'
                  : 'Log prices at 2+ stores to see comparisons'}
              </p>
              <p className="text-xs text-[#7A7468] mt-1">
                {storePricesData.length === 0
                  ? 'Save a store name when you confirm items to start tracking'
                  : `${comparisons.length} item${comparisons.length !== 1 ? 's' : ''} tracked so far`}
              </p>
            </div>
          )}
        </motion.div>

        {/* Savings opportunities */}
        {opportunities.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-[#7A7468] uppercase tracking-wide mb-3">
              Savings opportunities
            </p>
            {opportunities.map((opp, i) => (
              <OpportunityCard key={opp.item.itemId} opp={opp} index={i} salePriceMap={salePriceMap} />
            ))}
          </div>
        )}

        {/* All items with price data */}
        {comparisons.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-[#7A7468] uppercase tracking-wide mb-3">
              All tracked items
            </p>
            <div className="flex flex-col gap-2">
              {comparisons.map((c, i) => (
                <AllItemCard
                  key={c.itemId}
                  comparison={c}
                  index={i}
                  onTap={() => navigate('/home')}
                  cheapestWasSale={salePriceMap[`${c.itemId}-${c.stores[0].storeName}`] ?? false}
                />
              ))}
            </div>
          </div>
        )}

        {/* Store summary */}
        {userStores.length > 0 && (
          <div>
            <p className="text-xs font-semibold text-[#7A7468] uppercase tracking-wide mb-3">
              Your stores
            </p>
            <div className="bg-white rounded-2xl border border-[#E8E2DC] divide-y divide-[#F0EDE8] overflow-hidden">
              {userStores.map(store => (
                <div key={store.store_name} className="flex items-center justify-between px-4 py-3.5">
                  <div className="flex items-center gap-3">
                    <span className="text-lg">🏪</span>
                    <div>
                      <p className="text-sm font-medium text-[#2C2A26]">{store.store_name}</p>
                      <p className="text-xs text-[#B0A99F]">
                        {store.visit_count ?? 0} visit{store.visit_count !== 1 ? 's' : ''}
                        {store.last_visited && ` · last ${new Date(store.last_visited + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}`}
                      </p>
                    </div>
                  </div>
                  <button
                    onClick={() => toggleFavorite.mutate({ storeName: store.store_name, current: store.is_favorite })}
                    className="text-lg transition-transform hover:scale-110"
                    aria-label="Toggle favorite"
                  >
                    {store.is_favorite ? '⭐' : '☆'}
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}

        {storePricesData.length === 0 && userStores.length === 0 && (
          <div className="text-center py-12">
            <p className="text-5xl mb-4">🏪</p>
            <p className="text-base font-semibold text-[#2C2A26]">Start tracking store prices</p>
            <p className="text-sm text-[#7A7468] mt-2 max-w-[280px] mx-auto">
              When you confirm items or add them, enter the store name to build your price comparison
            </p>
          </div>
        )}

      </div>
    </div>
  )
}
